/**
 * <vat-toggle> — "Inc VAT / Ex VAT" price display switch (utility bar).
 *
 * The shop is tax-inclusive (Shopify: taxesIncluded = true) and UK is the
 * primary market, so every price Liquid renders already carries 20% VAT. Ex-VAT
 * is therefore a DISPLAY rewrite: nothing about the cart, the totals Shopify
 * calculates, or checkout changes. The snippet only renders the control for the
 * GB market — in Ireland/Germany/International the displayed price isn't UK
 * VAT-inclusive and dividing by 1.2 would simply be wrong.
 *
 * Three things worth knowing before editing:
 *
 * 1. EVERY price is labelled, in both modes — "inc VAT" or "ex VAT". So the
 *    sweep runs in inc mode too; it just doesn't do arithmetic there.
 *    Each marked element keeps the theme's own text (data-vat-src) alongside
 *    what we wrote (data-vat-out) and which mode it's in (data-vat-mode).
 *    "Already ours" is decided by those, not by a flag — that is what makes a
 *    second pass idempotent, so our own MutationObserver writes settle instead
 *    of dividing by 1.2 again, and a mode switch re-derives from the ORIGINAL
 *    rather than from a figure we already converted. Anything unmarked is fresh
 *    text from the theme, which is exactly right when it re-renders a price
 *    (variant change, cart re-render, quick filter).
 *
 * 2. <accessories-panel> is skipped entirely. It writes a running total into the
 *    theme's own price element and re-applies it from its own observer, so two
 *    writers enforcing different numbers would loop forever. Instead it formats
 *    its prices through window.SUKVat itself, and the element it is currently
 *    driving carries data-ap-base, which is in the skip list too.
 *
 * 3. The switch fires 'suk:vat:before' (hand any hijacked price back to the
 *    theme), then rewrites, then 'suk:vat' (re-apply in the new mode). The order
 *    is what lets the panel re-stash a base price in the mode we just moved to.
 */
(() => {
  const KEY = 'sukVatMode';

  /* Leaf elements whose text is a price. Deliberately NOT a blanket text-node
     walk: facet price filters display inc-VAT values that they also submit, and
     rewriting those would filter on a number the customer can't see. */
  const PRICE_SELECTOR = [
    '.price-item',
    '.cart-item__price-wrapper .price',
    '.product-option--price',
    '.totals__subtotal-value',
    '.totals__total-value',
    '.unit-price',
    '[data-vat-price]',
  ].join(', ');

  const SKIP = 'accessories-panel, .facets, facet-filters-form, [data-ap-base], [data-vat-skip]';

  const MONEY = /([£$€])\s?(\d[\d,]*(?:\.\d+)?)/g;

  const TAG = 'vat-tag';
  const LABEL = { inc: 'inc VAT', ex: 'ex VAT' };

  const hasMoney = (text) => /[£$€]\s?\d/.test(text);

  function tagNode() {
    const tag = document.createElement('span');
    tag.className = TAG;
    tag.textContent = LABEL[state.mode];
    return tag;
  }

  const money = (n) =>
    n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  /** Rewrite every money figure in a string to ex-VAT. Returns null when the
   *  string holds no price, so a template that renders "Sold out" is left be. */
  function toEx(text, rate) {
    let hit = false;
    const out = text.replace(MONEY, (m, symbol, number) => {
      const value = parseFloat(number.replace(/,/g, ''));
      if (!isFinite(value)) return m;
      hit = true;
      return symbol + money(value / (1 + rate));
    });
    return hit ? out : null;
  }

  const state = {
    mode: 'inc',
    rate: 0.2,
  };

  function targets() {
    return [...document.querySelectorAll(PRICE_SELECTOR)].filter(
      // A container that holds another price element isn't a leaf — rewriting
      // both would double-convert the inner one.
      (el) => !el.closest(SKIP) && !el.querySelector(PRICE_SELECTOR)
    );
  }

  /** Print an element's price in the current mode and label it. BOTH modes are
   *  labelled: an unlabelled price is the one a customer has to guess about, and
   *  "inc VAT" on a page someone landed on from Google is worth as much as the
   *  "ex VAT" that told them the switch is on (Ashley, 2026-09-21).
   *
   *  "Is this ours?" is `textContent === data-vat-out`, NEVER the presence of the
   *  tag: a struck-through compare-at price is deliberately left untagged, so
   *  asking the tag made every pass treat our own output as fresh input. In ex
   *  mode that divided the compare-at by 1.2 again on each observer tick —
   *  £10.41 → £8.68 → … → £0.03 on screen (live, 2026-09-21).
   *
   *  The theme's own text is kept in data-vat-src, so switching modes re-derives
   *  from the ORIGINAL rather than from what we already converted. */
  function mark(el) {
    const ours = el.dataset.vatOut !== undefined && el.textContent === el.dataset.vatOut;
    if (ours && el.dataset.vatMode === state.mode) return;

    const source = ours ? el.dataset.vatSrc : el.textContent;
    if (source === undefined || !hasMoney(source)) return;

    const body = state.mode === 'ex' ? toEx(source, state.rate) : source;
    if (body === null) return;

    el.dataset.vatSrc = source;
    // Trailing whitespace off: Liquid indents these spans, so the text node ends
    // in a newline that collapses to a full space before the tag — at a 40px PDP
    // price that space alone is ~10px, which no margin tweak can claw back. The
    // gap is the tag's margin and nothing else. (Writing text also drops the old
    // tag, which is how a mode change replaces it.)
    el.textContent = body.replace(/\s+$/, '');

    // No tag on a struck-through compare-at price: it sits immediately beside
    // the live price, which already carries one.
    if (!el.matches('s')) el.appendChild(tagNode());

    el.dataset.vatMode = state.mode;
    el.dataset.vatOut = el.textContent;
  }

  function sweep() {
    targets().forEach(mark);
  }

  let pending;
  function schedule() {
    clearTimeout(pending);
    pending = setTimeout(sweep, 120);
  }

  function setMode(mode, silent) {
    state.mode = mode === 'ex' ? 'ex' : 'inc';
    document.documentElement.setAttribute('data-vat-mode', state.mode);
    try {
      if (!silent) localStorage.setItem(KEY, state.mode);
    } catch (e) {
      /* private mode — the switch still works for this page view */
    }

    const detail = { mode: state.mode, rate: state.rate };
    document.dispatchEvent(new CustomEvent('suk:vat:before', { detail }));
    sweep();
    document.dispatchEvent(new CustomEvent('suk:vat', { detail }));
  }

  window.SUKVat = {
    get mode() {
      return state.mode;
    },
    get rate() {
      return state.rate;
    },
    /** Minor units (pence) in, minor units ex-VAT out — for anything that holds
     *  its own price data rather than reading it off the page. */
    exMinor(minor) {
      return Math.round(minor / (1 + state.rate));
    },
    /** The suffix every price carries, for code that formats its own money —
     *  the accessories panel, which the sweep skips. */
    get label() {
      return LABEL[state.mode];
    },
    tagHtml() {
      return `<span class="${TAG}">${LABEL[state.mode]}</span>`;
    },
    set: setMode,
  };

  class VatToggle extends HTMLElement {
    connectedCallback() {
      const rate = parseFloat(this.dataset.rate);
      if (isFinite(rate) && rate > 0) state.rate = rate;

      this.button = this.querySelector('.vat-toggle__switch');
      this.labels = [...this.querySelectorAll('.vat-toggle__label')];

      this._onClick = () => setMode(state.mode === 'ex' ? 'inc' : 'ex');
      this.button?.addEventListener('click', this._onClick);

      // More than one bar can carry the control (the sticky header duplicates
      // the utility bar on some layouts), so each instance renders from the
      // shared mode rather than from its own last click.
      this._onVat = () => this.paint();
      document.addEventListener('suk:vat', this._onVat);

      // Prices are rendered by the theme and re-rendered constantly: variant
      // change, cart drawer, predictive search, quick filter, infinite scroll.
      this._observer = new MutationObserver(schedule);
      this._observer.observe(document.body, { childList: true, subtree: true });

      let stored = null;
      try {
        stored = localStorage.getItem(KEY);
      } catch (e) {
        /* ignore */
      }
      setMode(stored === 'ex' ? 'ex' : 'inc', true);
      this.paint();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this._onClick);
      document.removeEventListener('suk:vat', this._onVat);
      this._observer?.disconnect();
    }

    paint() {
      const ex = state.mode === 'ex';
      this.button?.setAttribute('aria-checked', ex ? 'true' : 'false');
      this.labels.forEach((el) => el.classList.toggle('is-on', (el.dataset.mode === 'ex') === ex));
    }
  }

  if (!customElements.get('vat-toggle')) customElements.define('vat-toggle', VatToggle);
})();
